import { getPublicProfile } from "api/auth";
import { ApiError } from "api/client";
import { getUserPublicTrees } from "api/trees";
import type { ExploreTreeDto, MatchedEntityDto, PublicProfileDto } from "api/types";
import { UserAvatar } from "components/UserAvatar";
import { useBrand } from "hooks/useBrand";
import { useSeo } from "hooks/useSeo";
import { ExploreSearchBar, ExploreTreeCard } from "pages/ExplorePage/components";
import type { ExploreCard } from "pages/ExplorePage/explore.types";
import { defaultCoverPreview } from "pages/HomePage/data";
import TopNav from "pages/TreeViewPage/TopNav";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getUserProfilePath } from "routes/Paths";
import { formatLastModified, translateUtcToLocalTime } from "utilities";

import styles from "./UserProfilePage.module.css";

const PAGE_SIZE = 24;

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

const toCard = (tree: ExploreTreeDto, noDescription: string): ExploreCard => ({
	cover: tree.coverImageUrl?.trim() || defaultCoverPreview,
	description: tree.description?.trim() || noDescription,
	entityCount: tree.entityCount,
	id: tree.id,
	isLiked: tree.isLikedByMe,
	lastEditedLabel: formatLastModified(tree.lastModifiedDate),
	likes: tree.likeCount,
	matches: tree.matchedEntities.map((entity: MatchedEntityDto) => ({
		id: entity.id,
		label: entity.title,
		type: entity.entityType
	})),
	ownerImageUrl: tree.ownerProfileImageUrl,
	ownerInitials: getInitials(tree.ownerName),
	ownerName: tree.ownerName,
	ownerUserId: tree.ownerUserId,
	stars: tree.starCount,
	status: tree.visibility,
	title: tree.title
});

const formatMemberSince = (value: string): string => {
	const date = translateUtcToLocalTime(value);

	return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString([], { month: "long", year: "numeric" });
};

/**
 * Public profile of a user: their name, photo, and public trees. The email only appears when the
 * profile owner opted in via Settings; everyone else (and anonymous visitors) never sees it.
 */
const UserProfilePage = () => {
	const { t } = useTranslation(["userProfilePage", "seo"]);
	const { id } = useParams<{ id: string }>();
	const { brand } = useBrand();
	const [profile, setProfile] = useState<PublicProfileDto | null>(null);
	const [cards, setCards] = useState<ExploreCard[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isTreesLoading, setIsTreesLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [isNotFound, setIsNotFound] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [pageIndex, setPageIndex] = useState(1);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [searchInput, setSearchInput] = useState("");

	// Indexable only once the profile has actually loaded: a not-found or errored profile page carries
	// no content worth a search result, and its owner's name must not appear in a meta tag before the
	// API has confirmed the profile is public.
	useSeo({
		title: profile ? t("seo:profile.title", { name: profile.fullName }) : null,
		description: profile
			? t(profile.publicTreeCount > 0 ? "seo:profile.descriptionWithCount" : "seo:profile.descriptionEmpty", {
					brand,
					count: profile.publicTreeCount,
					name: profile.fullName
			  })
			: null,
		canonicalPath: id ? getUserProfilePath(id) : null,
		imageUrl: profile?.profileImageUrl,
		type: "profile",
		noIndex: !profile
	});
	const [submittedSearch, setSubmittedSearch] = useState("");

	// The profile header loads once per user; searching only refetches the tree grid below it.
	useEffect(() => {
		if (!id) {
			return undefined;
		}

		let isCurrent = true;

		const load = async (): Promise<void> => {
			setIsLoading(true);
			setIsNotFound(false);
			setLoadError("");
			setProfile(null);
			setSearchInput("");
			setSubmittedSearch("");

			try {
				// The endpoint is anonymous-readable, so no token is needed here.
				const loadedProfile = await getPublicProfile(id, "");
				if (isCurrent) {
					setProfile(loadedProfile);
				}
			} catch (error) {
				if (!isCurrent) {
					return;
				}

				if (error instanceof ApiError && error.status === 404) {
					setIsNotFound(true);
				} else {
					setLoadError(error instanceof Error ? error.message : t("loadError"));
				}
			} finally {
				if (isCurrent) {
					setIsLoading(false);
				}
			}
		};

		load().catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [id, t]);

	useEffect(() => {
		if (!id) {
			return undefined;
		}

		let isCurrent = true;

		const load = async (): Promise<void> => {
			setIsTreesLoading(true);

			try {
				const trees = await getUserPublicTrees(
					id,
					{ pageIndex: 1, pageSize: PAGE_SIZE, search: submittedSearch || undefined },
					""
				);

				if (!isCurrent) {
					return;
				}

				setCards(trees.items.map((tree: ExploreTreeDto) => toCard(tree, t("noDescription"))));
				setPageIndex(trees.pageIndex);
				setHasNextPage(trees.hasNextPage);
			} catch {
				// The page-level error/not-found state comes from the profile load; an empty grid is enough here.
				if (isCurrent) {
					setCards([]);
					setHasNextPage(false);
				}
			} finally {
				if (isCurrent) {
					setIsTreesLoading(false);
				}
			}
		};

		load().catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [id, submittedSearch, t]);

	const onSearch = useCallback((term: string): void => setSubmittedSearch(term), []);

	const onLoadMore = useCallback(async (): Promise<void> => {
		if (!id || isLoadingMore) {
			return;
		}

		setIsLoadingMore(true);
		try {
			const trees = await getUserPublicTrees(
				id,
				{ pageIndex: pageIndex + 1, pageSize: PAGE_SIZE, search: submittedSearch || undefined },
				""
			);
			const nextCards = trees.items.map((tree: ExploreTreeDto) => toCard(tree, t("noDescription")));

			setCards((current: ExploreCard[]) => [...current, ...nextCards]);
			setPageIndex(trees.pageIndex);
			setHasNextPage(trees.hasNextPage);
		} catch {
			// Keep the current page; the user can retry Load more.
		} finally {
			setIsLoadingMore(false);
		}
	}, [id, isLoadingMore, pageIndex, submittedSearch, t]);

	const memberSinceLabel = profile ? formatMemberSince(profile.memberSince) : "";

	let message = "";
	if (isLoading) {
		message = t("loading");
	} else if (isNotFound) {
		message = t("notFound");
	} else if (loadError) {
		message = loadError;
	}

	let treesMessage = "";
	if (isTreesLoading && cards.length === 0) {
		treesMessage = t("loadingTrees");
	} else if (cards.length === 0) {
		treesMessage = submittedSearch
			? t("emptySearch", { term: submittedSearch })
			: t("emptyTrees", { name: profile?.fullName ?? "" });
	}

	return (
		<div className={styles.page}>
			<TopNav />

			<main className={styles.content}>
				{message ? (
					<p className={styles.message}>{message}</p>
				) : (
					profile && (
						<>
							<section className={styles.profileCard}>
								<UserAvatar
									className={styles.avatar}
									imageUrl={profile.profileImageUrl}
									initials={getInitials(profile.fullName)}
								/>
								<div className={styles.identity}>
									<h1 className={styles.name}>{profile.fullName}</h1>
									{profile.email && (
										<a className={styles.email} href={`mailto:${profile.email}`}>
											{profile.email}
										</a>
									)}
									<p className={styles.meta}>
										{memberSinceLabel && (
											<span>{t("memberSince", { date: memberSinceLabel })}</span>
										)}
										{memberSinceLabel && <span className={styles.metaDot}>•</span>}
										<span>{t("publicTreeCount", { count: profile.publicTreeCount })}</span>
									</p>
								</div>
							</section>

							<div className={styles.searchRow}>
								<ExploreSearchBar
									isSearching={isTreesLoading}
									onSearch={onSearch}
									onValueChange={setSearchInput}
									value={searchInput}
								/>
							</div>

							<h2 className={styles.treesHeading}>
								{submittedSearch ? t("resultsFor", { term: submittedSearch }) : t("treesHeading")}
							</h2>

							{treesMessage ? (
								<p className={styles.message}>{treesMessage}</p>
							) : (
								<>
									<section aria-label={t("treesHeading")} className={styles.grid}>
										{cards.map((card: ExploreCard) => (
											<ExploreTreeCard key={card.id} card={card} />
										))}
									</section>

									{hasNextPage && (
										<div className={styles.loadMoreRow}>
											<button
												className={styles.loadMoreButton}
												disabled={isLoadingMore}
												onClick={onLoadMore}
												type="button">
												{isLoadingMore ? t("loadingMore") : t("loadMore")}
											</button>
										</div>
									)}
								</>
							)}
						</>
					)
				)}
			</main>
		</div>
	);
};

export default UserProfilePage;
